import { motion } from "framer-motion";
import { HelpCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const faqs = [
  {
    q: "How much do your services cost?",
    a: "Simple tax preparation and consulting starts at $150. Depending on the service, most engagements run $200–$500, and our comprehensive full-service package is $4,979. Every engagement still begins with a free 30-minute consultation, after which you receive a clear, fixed quote — you approve the price before any work begins.",
  },
  {
    q: "What happens in the free consultation?",
    a: "We spend 30 minutes understanding your situation: the state of your books, your tax picture, and your goals. You leave with honest recommendations you can act on — whether or not you hire us. There is no obligation and no sales pressure.",
  },
  {
    q: "How quickly can you get started, and how long do things take?",
    a: "Most clients are onboarded within a week of the consultation. A typical bookkeeping catch-up or clean-up takes 2–4 weeks depending on how far behind the books are. Individual tax returns are usually completed within 1–2 weeks of receiving all documents; business returns within 2–3 weeks. Fractional CFO engagements begin with a 30-day financial assessment.",
  },
  {
    q: "Do you work with clients outside of Texas?",
    a: "Yes. BookCare LLC serves clients in person anywhere in the USA and remotely. Most bookkeeping, tax, and CFO work is handled securely online with scheduled video calls, so distance is never a barrier.",
  },
  {
    q: "What accounting software do you support?",
    a: "We are QuickBooks Online specialists (ProAdvisor certified) and also work with common nonprofit and small-business stacks. If your books live in spreadsheets today, we will migrate you to a clean, reliable system as part of onboarding.",
  },
  {
    q: "Do you specialize in nonprofits?",
    a: "Yes — nonprofit finance is a core strength. Zecharias has led finance for multiple nonprofits, managed federal grants under OMB 2 CFR 200, and taken Single Audits to unmodified (clean) opinions. Fund accounting, grant compliance, and board-ready reporting are everyday work for us.",
  },
  {
    q: "What is the career coaching program and who is it for?",
    a: "It is one-on-one mentorship for immigrant and internationally trained professionals who want to build a finance or accounting career in the US — covering credential positioning, resume and interview preparation, and the unwritten rules of the American workplace. Coaching engagements are tailored to your starting point; book a consultation to discuss fit.",
  },
  {
    q: "Is my financial information safe with you?",
    a: "Yes. Documents are exchanged through secure, encrypted channels — never plain email attachments — and your data is only accessed by the people working on your engagement. We never ask for passwords or full card details by email.",
  },
];

export default function FAQ() {
  return (
    <section id="faq" data-testid="faq-section" className="border-t border-linen bg-white/40">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
            >
              <HelpCircle size={14} /> Common Questions
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
              className="mb-6 font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl"
              data-testid="faq-heading"
            >
              Before you <span className="italic text-forest">book</span>.
            </motion.h2>
            <p className="text-sm leading-relaxed text-pine/60">
              Straight answers on pricing, timelines, and what working together looks like.
              Something else on your mind? The consultation is free — just ask.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="lg:col-span-8"
          >
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-linen">
                  <AccordionTrigger
                    data-testid={`faq-question-${i}`}
                    className="py-6 text-left font-serif text-lg font-semibold text-pine hover:text-forest hover:no-underline md:text-xl"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-answer-${i}`}
                    className="pb-6 text-base leading-relaxed text-pine/70"
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <a
              href="#booking"
              data-testid="faq-booking-cta"
              className="mt-10 inline-block border border-forest bg-forest px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-forest"
            >
              Still curious? Book your free consultation
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
